import React from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, Headphones, Info, Minimize, Maximize, VolumeX, Sliders, Cloud, ChevronDown, ChevronUp, Download, Eye, EyeOff, Brain, X, Square } from 'lucide-react';
import { Slider } from "@/components/ui/slider";
import { PresetInfo } from './PresetCard';
import DownloadButton from './DownloadButton';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { AmbienceSound } from './AmbienceMixer';
import { Progress } from "@/components/ui/progress";
import { getSoundUrl, ambientSoundPresets } from '@/config/audioConfig';
import { getBrainwaveCategory } from './FrequencySlider';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PlayerProps {
  activePreset: PresetInfo | null;
  isPlaying: boolean;
  volume?: number;
  onVolumeChange?: (value: number) => void;
  masterVolume?: number;
  onMasterVolumeChange?: (value: number) => void;
  onPlayPause: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onComplete: () => void;
  onStartRecording: () => boolean;
  onStopRecording: () => Promise<Blob | null>;
  onDirectDownload?: (
    presetName: string, 
    frequency: number, 
    duration: number, 
    ambientSounds?: AmbienceSound[]
  ) => Promise<void>;
  ambientSounds?: AmbienceSound[];
  onAmbientVolumeChange?: (id: string, volume: number) => void;
  onTogglePlayMode?: () => void;
  isPlayMode?: boolean;
  advancedSettings?: {
    leftFrequency: number;
    rightFrequency: number;
    carrierFrequency: number;
    leftWaveform?: string;
    rightWaveform?: string;
  };
  displayFrequency?: number;
  isAdvancedSession?: boolean;
  currentAdvancedStep?: number;
  totalAdvancedSteps?: number;
  onSeek?: (timeInSeconds: number) => void;
  onAmbientSeek?: (timeInSeconds: number) => void;
  onClose?: () => void;
}

const Player = React.forwardRef<HTMLDivElement, PlayerProps>(({ 
  activePreset, 
  isPlaying, 
  volume = 33,
  onVolumeChange,
  masterVolume = 33,
  onMasterVolumeChange,
  onPlayPause,
  onNext,
  onPrevious,
  onComplete,
  onStartRecording,
  onStopRecording,
  onDirectDownload,
  ambientSounds = [],
  onAmbientVolumeChange,
  onTogglePlayMode,
  isPlayMode = false,
  displayFrequency,
  isAdvancedSession,
  currentAdvancedStep,
  totalAdvancedSteps,
  onSeek,
  onAmbientSeek,
  onClose
}, ref) => {
  const [showMobileHint, setShowMobileHint] = React.useState(
    window.innerWidth < 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  );
  
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [presetLoaded, setPresetLoaded] = React.useState(false);
  const [timeRemaining, setTimeRemaining] = React.useState(0);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);
  const [isVolumeControlsExpanded, setIsVolumeControlsExpanded] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const seekBarRef = React.useRef<HTMLDivElement>(null);
  const [showCloseDialog, setShowCloseDialog] = React.useState(false);
  const [isRecording, setIsRecording] = React.useState(false);
  const [isPreviewing, setIsPreviewing] = React.useState(false);

  React.useEffect(() => {
    if (activePreset) {
      console.log('Player - Active preset loaded:', activePreset.name);
      setPresetLoaded(true);
      
      if (activePreset.ambientSounds) {
        console.log('Player - Preset ambient sounds configuration:', activePreset.ambientSounds);
        
        // Initialize any missing sounds from the preset
        activePreset.ambientSounds.forEach(presetSound => {
          const stateSound = ambientSounds.find(s => s.id === presetSound.id);
          
          if (!stateSound) {
            // Find the sound in ambientSoundPresets
            const configSound = ambientSoundPresets.find(s => s.id === presetSound.id);
            if (configSound) {
              console.log(`Initializing missing sound ${presetSound.id} from config`);
              // The sound will be initialized in the parent component
              // We just need to log that it's missing
            } else {
              console.warn(`Sound ${presetSound.id} defined in preset but not found in config!`);
            }
          } else {
            console.log(`Sound ${presetSound.id}: preset volume=${presetSound.volume}, state volume=${stateSound.volume}, path=${stateSound.path || 'not set'}`);
          }
        });
      }
    }
  }, [activePreset, ambientSounds]);
  
  React.useEffect(() => {
    if (activePreset && activePreset.ambientSounds) {
      console.log('Player - Active preset ambient sounds configuration:', activePreset.ambientSounds);
      
      
      const activeSoundsFromState = ambientSounds.filter(sound => sound.volume > 0);
      console.log('Player - Current active ambient sounds from state:', activeSoundsFromState);
      
      activePreset.ambientSounds.forEach(presetSound => {
        const stateSound = ambientSounds.find(s => s.id === presetSound.id);
        console.log(`Sound ${presetSound.id}: preset volume=${presetSound.volume}, state volume=${stateSound?.volume || 'not found'}, path=${stateSound?.path || 'not set'}`);
      });
    }
  }, [activePreset, ambientSounds, masterVolume]);
  
  React.useEffect(() => {
    if (activePreset) {
      setTimeRemaining(activePreset.duration * 60);
    }
  }, [activePreset?.duration, activePreset?.id]);

  React.useEffect(() => {
    if (isPlaying && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            onComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying, timeRemaining, onComplete]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    
    requestAnimationFrame(() => {
      onPlayPause();
      
      setTimeout(() => {
        setIsProcessing(false);
      }, 300);
    });
  };

  const getSoundPath = (sound: { id: string; path?: string; }): string => {
    // First check if the sound has a path in the current state
    if (sound.path && sound.path !== '') {
      return sound.path;
    }
    
    // Then check the ambientSoundPresets config
    const configSound = ambientSoundPresets.find(s => s.id === sound.id);
    if (configSound && configSound.path) {
      return configSound.path;
    }
    
    console.error(`No path found for sound ${sound.id}`);
    return "Unknown";
  };

  if (!activePreset) return null;

  const activeAmbientSounds = activePreset.ambientSounds
    ? activePreset.ambientSounds.map(presetSound => {
        // Find the current state of this sound
        const currentStateSound = ambientSounds.find(s => s.id === presetSound.id);
        
        // Find the config for this sound
        const configSound = ambientSoundPresets.find(s => s.id === presetSound.id);
        
        // Get the name from current state, config, or generate from ID
        const name = currentStateSound?.name || configSound?.name || presetSound.id.charAt(0).toUpperCase() + presetSound.id.slice(1);
        
        // Get the path from current state or config
        const path = getSoundPath({ 
          id: presetSound.id, 
          path: currentStateSound?.path 
        });
        
        // Get the icon from current state or config
        const icon = currentStateSound?.icon || configSound?.icon || Cloud;
        
        // Use preset volume if no current state, otherwise use current state volume
        const volume = currentStateSound?.volume ?? presetSound.volume;
        
        return {
          ...presetSound,
          name,
          path,
          icon,
          volume
        };
      })
    : [];
  
  // Calculate progress percentage
  const progress = activePreset ? ((activePreset.duration * 60 - timeRemaining) / (activePreset.duration * 60)) * 100 : 0;

  // Handle seek bar click
  const handleSeekBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!activePreset || !seekBarRef.current || isDragging) return;

    const rect = seekBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = Math.floor(percentage * activePreset.duration * 60);
    
    // Update the time remaining
    setTimeRemaining(activePreset.duration * 60 - newTime);
    
    // Call the seek handlers
    onSeek?.(newTime);
    onAmbientSeek?.(newTime);
  };

  // Handle seek bar drag
  const handleSeekBarDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!activePreset || !seekBarRef.current) return;
    
    const rect = seekBarRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = x / rect.width;
    const newTime = Math.floor(percentage * activePreset.duration * 60);
    
    // Update the time remaining
    setTimeRemaining(activePreset.duration * 60 - newTime);
    
    // Call the seek handlers
    onSeek?.(newTime);
    onAmbientSeek?.(newTime);
  };

  const handleClose = () => {
    if (isPlaying) {
      setShowCloseDialog(true);
    } else {
      onClose?.();
    }
  };

  const handleConfirmClose = () => {
    // Stop playback first
    if (isPlaying) {
      onPlayPause();
    }
    // Close the player
    onClose?.();
  };

  return (
    <>
      <div 
        ref={ref} 
        className={`player-card glass rounded-3xl p-6 animate-fade-in transition-all duration-300 ${
          isPlayMode 
            ? 'fixed inset-0 z-50 m-auto max-w-4xl mx-auto w-[calc(100%-2rem)] sm:w-[calc(100%-4rem)] h-fit max-h-[90vh] overflow-y-auto overflow-x-hidden' 
            : ''
        }`}
      >
        {showMobileHint && !isPlayMode && (
          <Alert className="mb-4 bg-secondary/30">
            <Headphones className="h-5 w-5 text-primary" />
            <AlertDescription className="text-sm">
              For the best experience, use headphones or earbuds and turn your device's sound on.
            </AlertDescription>
          </Alert>
        )}
        
        {!isPlayMode && (
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-focus to-meditation flex items-center justify-center">
                <Brain className="h-4 w-4 text-white" />
              </div>
              <h2 className="text-lg font-medium tracking-tight">BinauralFlow Player</h2>
            </div>
            {onTogglePlayMode && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 px-2 text-muted-foreground hover:text-foreground"
                    onClick={onTogglePlayMode}
                  >
                    {isPlayMode ? (
                      <Minimize className="h-4 w-4 mr-1" />
                    ) : (
                      <Maximize className="h-4 w-4 mr-1" />
                    )}
                    {isPlayMode ? 'Exit Focus Mode' : 'Enter Focus Mode'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="end" className="z-[100]">
                  <p>{isPlayMode ? 'Press ESC to exit focus mode' : 'Enter fullscreen mode for distraction-free listening'}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
        
        {isPlayMode && (
          <div className="flex justify-end mb-2">
            {onTogglePlayMode && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 px-2 text-muted-foreground hover:text-foreground"
                    onClick={onTogglePlayMode}
                  >
                    <Minimize className="h-4 w-4 mr-1" />
                    Exit Focus Mode
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="end" className="z-[100]">
                  <p>Press ESC to exit focus mode</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
        
        <div className="mb-6 w-full">
          <div className="h-24 rounded-xl flex items-center justify-center bg-secondary/20">
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full animate-pulse-slow" style={{ backgroundColor: activePreset.color }}></div>
                  <div className="text-xl font-bold text-foreground">
                    {getBrainwaveCategory(displayFrequency)}
                  </div>
                </div>
              </div>
              <div className="text-sm font-mono text-muted-foreground">
                {displayFrequency.toFixed(1)} Hz
              </div>
              <Progress className="w-48 h-2" value={isPlaying ? 75 : 0} />
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-medium mb-1">{activePreset.name}</h3>
            <p className="text-sm text-muted-foreground mb-6">{activePreset.description}</p>
            
            <div className="flex items-center justify-center gap-4 mb-4">
              <button 
                onClick={onPrevious}
                className="h-10 w-10 rounded-full flex items-center justify-center transition-colors hover:bg-secondary"
                disabled={!onPrevious || isProcessing}
              >
                <SkipBack className="h-5 w-5" />
              </button>
              
              <button 
                onClick={handlePlayPause}
                className="h-12 w-12 rounded-full flex items-center justify-center bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
                disabled={isProcessing}
              >
                {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
              </button>
              
              <button 
                onClick={onNext}
                className="h-10 w-10 rounded-full flex items-center justify-center transition-colors hover:bg-secondary"
                disabled={!onNext || isProcessing}
              >
                <SkipForward className="h-5 w-5" />
              </button>
            </div>

            {/* Seek Bar */}
            <div className="relative mb-4">
              <div 
                ref={seekBarRef}
                className="h-2 bg-secondary rounded-full cursor-pointer relative"
                onClick={handleSeekBarClick}
                onMouseDown={() => setIsDragging(true)}
                onMouseUp={() => setIsDragging(false)}
                onMouseLeave={() => setIsDragging(false)}
                onMouseMove={isDragging ? handleSeekBarDrag : undefined}
              >
                <div 
                  className="absolute left-0 top-0 h-full bg-primary rounded-full transition-all duration-100"
                  style={{ width: `${progress}%` }}
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-sm cursor-pointer hover:scale-110 transition-transform"
                      style={{ left: `${progress}%` }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{formatTime(timeRemaining)} remaining</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>{formatTime(activePreset.duration * 60 - timeRemaining)}</span>
                <span>{formatTime(timeRemaining)}</span>
              </div>
            </div>

            <div className="flex justify-center gap-4">
              <DownloadButton
                presetName={activePreset.name}
                frequency={activePreset.frequency}
                duration={activePreset.duration}
                ambientSounds={activeAmbientSounds}
                onDirectDownload={onDirectDownload}
              />
              {!isPlayMode && onClose && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={handleClose}
                >
                  <Square className="h-4 w-4 mr-2" />
                  Stop Session
                </Button>
              )}
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Session Info</span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Frequency</span>
                <span className="font-mono">{activePreset.frequency} Hz</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration</span>
                <span>{activePreset.duration} minutes</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Time Remaining</span>
                <span className="font-mono">{formatTime(timeRemaining)}</span>
              </div>
              {isAdvancedSession && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Step</span>
                  <span>{currentAdvancedStep ? currentAdvancedStep + 1 : 1} of {totalAdvancedSteps}</span>
                </div>
              )}
            </div>

            {/* Volume Controls Toggle Button */}
            <div className="flex items-center justify-between pt-4 border-t border-border/30">
              <div className="flex items-center gap-2">
                <Volume2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Volume Controls</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setIsVolumeControlsExpanded(!isVolumeControlsExpanded)}
              >
                {isVolumeControlsExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Collapsible Volume Controls */}
            <div className={`space-y-6 overflow-hidden transition-all duration-300 ease-in-out ${
              isVolumeControlsExpanded ? 'max-h-[600px] opacity-100 pb-4' : 'max-h-0 opacity-0'
            }`}>
              {/* Master Volume */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Master Volume</span>
                </div>
                <Slider
                  value={[masterVolume]}
                  onValueChange={([value]) => onMasterVolumeChange?.(value)}
                  max={100}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>0%</span>
                  <span>{masterVolume}%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* Binaural Beat Volume */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Binaural Beat Volume</span>
                </div>
                <Slider
                  value={[volume]}
                  onValueChange={([value]) => onVolumeChange?.(value)}
                  max={100}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>0%</span>
                  <span>{volume}%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* Ambient Sound Controls */}
              {activeAmbientSounds.length > 0 && onAmbientVolumeChange && (
                <div className="space-y-2">
                  {activeAmbientSounds.some(sound => {
                    const currentSound = ambientSounds?.find(s => s.id === sound.id);
                    return (currentSound?.volume || sound.volume) > 0;
                  }) && (
                    <div className="flex items-center gap-2">
                      <Sliders size={16} className="text-primary" />
                      <span className="text-sm font-medium">Ambient Sound Controls</span>
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    {activeAmbientSounds
                      .filter(sound => {
                        const currentSound = ambientSounds?.find(s => s.id === sound.id);
                        return (currentSound?.volume || sound.volume) > 0;
                      })
                      .map((sound) => {
                        const currentSound = ambientSounds?.find(s => s.id === sound.id);
                        const currentVolume = currentSound?.volume || sound.volume;
                        const soundPath = getSoundPath(sound);
                        
                        return (
                          <div key={sound.id} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm">{sound.name}</span>
                              <span className="text-xs text-muted-foreground">{currentVolume}%</span>
                            </div>
                            <Slider
                              value={[currentVolume]}
                              onValueChange={([value]) => onAmbientVolumeChange(sound.id, value)}
                              max={100}
                              step={1}
                              className="w-full"
                            />
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {!isPlayMode && (
          <div className="mt-6 flex items-center justify-center">
            <div className="bg-secondary/30 rounded-full px-4 py-2 flex items-center gap-2 text-sm text-muted-foreground">
              <Headphones className="h-4 w-4 text-primary" />
              <span>Use headphones or earbuds for best results</span>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop Session?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to stop the current session? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>Stop Session</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

Player.displayName = 'Player';

export default Player;
